import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Phone, Mail, ChevronDown } from 'lucide-react';
import { FaTelegram, FaWhatsapp } from 'react-icons/fa';
import { useCity } from '../contexts/CityContext';

const TopBar: React.FC = () => {
  const [showNotification, setShowNotification] = useState(false);
  const [isCityDropdownOpen, setIsCityDropdownOpen] = useState(false);
  const { selectedCity, setSelectedCity } = useCity();

  // Список городов РФ (основные)
  const cities = [
    'Москва', 'Санкт-Петербург', 'Новосибирск', 'Екатеринбург', 'Казань',
    'Нижний Новгород', 'Челябинск', 'Самара', 'Ростов-на-Дону', 'Уфа',
    'Красноярск', 'Пермь', 'Воронеж', 'Волгоград', 'Краснодар',
    'Саратов', 'Тюмень', 'Тольятти', 'Ижевск', 'Барнаул',
    'Ульяновск', 'Иркутск', 'Хабаровск', 'Ярославль', 'Владивосток',
    'Махачкала', 'Томск', 'Оренбург', 'Кемерово', 'Новокузнецк',
    'Рязань', 'Астрахань', 'Набережные Челны', 'Пенза', 'Липецк',
    'Киров', 'Чебоксары', 'Тула', 'Калининград', 'Балашиха',
    'Курск', 'Улан-Удэ', 'Ставрополь', 'Сочи', 'Иваново',
    'Брянск', 'Белгород', 'Архангельск', 'Владимир', 'Севастополь',
    'Чита', 'Грозный', 'Калуга', 'Смоленск', 'Вологда',
    'Курган', 'Орёл', 'Владикавказ', 'Череповец', 'Волжский',
    'Мурманск', 'Саранск', 'Стерлитамак', 'Глазов', 'Йошкар-Ола',
    'Омск' // Добавляем Омск в конец для удобства
  ];

  const handleTelegramClick = () => {
    return;
  };

  const handleWhatsappClick = () => {
    window.open('https://wa.me/79131544311', '_blank');
  };

  const cityDropdownRef = useRef<HTMLDivElement>(null);

  const handleCitySelect = (city: string) => {
    setSelectedCity(city);
    setIsCityDropdownOpen(false);
  };

  // Закрываем выпадающий список при клике вне его
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (cityDropdownRef.current && !cityDropdownRef.current.contains(event.target as Node)) {
        setIsCityDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleEmailClick = async () => {
    const email = 'proteko.rus@mail.ru';
    try {
      await navigator.clipboard.writeText(email);
      setShowNotification(true);
      setTimeout(() => setShowNotification(false), 3000);
    } catch (err) {
      console.error('Failed to copy email: ', err);
    }
  };

  const TelegramIcon = FaTelegram as React.ComponentType<{ className?: string }>;
  const WhatsAppIcon = FaWhatsapp as React.ComponentType<{ className?: string }>;

  return (
    <>
      <div className="hidden md:block bg-gray-900 text-white text-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-10">
            <div className="flex items-center space-x-6">
              <div className="relative" ref={cityDropdownRef}>
                <button
                  onClick={() => setIsCityDropdownOpen(!isCityDropdownOpen)}
                  className="flex items-center space-x-2 hover:text-primary-400 transition-colors duration-200 cursor-pointer"
                >
                  <MapPin className="w-4 h-4 text-primary-400" />
                  <span>{selectedCity}</span>
                  <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${isCityDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isCityDropdownOpen && (
                  <div className="absolute top-full left-0 mt-2 w-48 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-[9999] max-h-60 overflow-y-auto">
                    <div className="p-2">
                      {cities.map((city) => (
                        <button
                          key={city}
                          onClick={() => handleCitySelect(city)}
                          className={`w-full text-left px-3 py-2 text-sm rounded hover:bg-gray-700 transition-colors duration-200 ${
                            city === selectedCity ? 'text-primary-400 bg-gray-700' : 'text-gray-300'
                          }`}
                        >
                          {city}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <a 
                href="tel:+79131544311"
                className="flex items-center space-x-2 hover:text-primary-400 transition-colors duration-200 cursor-pointer"
              >
                <Phone className="w-4 h-4 text-primary-400" />
                <span>+7 (913) 154-43-11</span>
              </a>
              <button
                onClick={handleEmailClick}
                className="flex items-center space-x-2 hover:text-primary-400 transition-colors duration-200 cursor-pointer"
              >
                <Mail className="w-4 h-4 text-primary-400" />
                <span>proteko.rus@mail.ru</span>
              </button>
            </div>
            
            <div className="flex items-center space-x-4">
              <button
                onClick={handleTelegramClick}
                className="flex items-center space-x-2 hover:text-blue-400 transition-colors duration-200"
              >
                <TelegramIcon className="w-4 h-4" />
                <span className="hidden sm:inline">Telegram</span>
              </button>
              <button
                onClick={handleWhatsappClick}
                className="flex items-center space-x-2 hover:text-green-400 transition-colors duration-200"
              >
                <WhatsAppIcon className="w-4 h-4" />
                <span className="hidden sm:inline">WhatsApp</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Notification Toast */}
      {showNotification && (
        <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50">
          <div className="bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg flex items-center space-x-2 animate-bounce">
            <Mail className="w-4 h-4" />
            <span>Email скопирован в буфер обмена!</span>
          </div>
        </div>
      )}
    </>
  );
};

export default TopBar;
